import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { authFetch, formatApiError } from '../utils/api';
import { Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const SECTIONS = [
  {
    key: 'hero',
    title_en: 'Hero (Top of homepage)',
    title_el: 'Κεντρική Ενότητα',
    fields: [
      { name: 'title1', label_en: 'Main Title', label_el: 'Κύριος Τίτλος', type: 'text' },
      { name: 'title2', label_en: 'Highlighted Title', label_el: 'Τίτλος Έμφασης', type: 'text' },
      { name: 'subtitle', label_en: 'Subtitle', label_el: 'Υπότιτλος', type: 'textarea' },
      { name: 'cta', label_en: 'CTA Button', label_el: 'Κουμπί CTA', type: 'text' },
      { name: 'partnership', label_en: 'Partnership Caption', label_el: 'Λεζάντα Συνεργασίας', type: 'text' },
    ]
  },
  {
    key: 'features',
    title_en: 'Features Section',
    title_el: 'Ενότητα Χαρακτηριστικών',
    fields: [
      { name: 'advanced_badge', label_en: '— Section 1: Badge', label_el: '— Ενότητα 1: Σήμα', type: 'text' },
      { name: 'advanced_title', label_en: 'Section 1: Title', label_el: 'Ενότητα 1: Τίτλος', type: 'text' },
      { name: 'advanced_description', label_en: 'Section 1: Description', label_el: 'Ενότητα 1: Περιγραφή', type: 'textarea' },
      { name: 'advanced_cta', label_en: 'Section 1: CTA', label_el: 'Ενότητα 1: CTA', type: 'text' },
      { name: 'nextgen_badge', label_en: '— Section 2: Badge', label_el: '— Ενότητα 2: Σήμα', type: 'text' },
      { name: 'nextgen_title1', label_en: 'Section 2: Title (line 1)', label_el: 'Ενότητα 2: Τίτλος (γρ. 1)', type: 'text' },
      { name: 'nextgen_title2', label_en: 'Section 2: Title (line 2)', label_el: 'Ενότητα 2: Τίτλος (γρ. 2)', type: 'text' },
      { name: 'nextgen_description', label_en: 'Section 2: Description', label_el: 'Ενότητα 2: Περιγραφή', type: 'textarea' },
      { name: 'nextgen_cta', label_en: 'Section 2: CTA', label_el: 'Ενότητα 2: CTA', type: 'text' },
      { name: 'alerts_badge', label_en: '— Section 3: Badge', label_el: '— Ενότητα 3: Σήμα', type: 'text' },
      { name: 'alerts_title1', label_en: 'Section 3: Title (line 1)', label_el: 'Ενότητα 3: Τίτλος (γρ. 1)', type: 'text' },
      { name: 'alerts_title2', label_en: 'Section 3: Title (line 2)', label_el: 'Ενότητα 3: Τίτλος (γρ. 2)', type: 'text' },
      { name: 'alerts_description', label_en: 'Section 3: Description', label_el: 'Ενότητα 3: Περιγραφή', type: 'textarea' },
      { name: 'alerts_cta', label_en: 'Section 3: CTA', label_el: 'Ενότητα 3: CTA', type: 'text' },
    ]
  },
  {
    key: 'cta',
    title_en: 'Final CTA Block',
    title_el: 'Τελικό CTA Block',
    fields: [
      { name: 'explore_title', label_en: 'Left card: Title', label_el: 'Αριστερό card: Τίτλος', type: 'text' },
      { name: 'explore_desc', label_en: 'Left card: Description', label_el: 'Αριστερό card: Περιγραφή', type: 'textarea' },
      { name: 'explore_cta', label_en: 'Left card: CTA', label_el: 'Αριστερό card: CTA', type: 'text' },
      { name: 'plan_title', label_en: 'Right card: Title', label_el: 'Δεξί card: Τίτλος', type: 'text' },
      { name: 'plan_desc', label_en: 'Right card: Description', label_el: 'Δεξί card: Περιγραφή', type: 'textarea' },
      { name: 'plan_cta', label_en: 'Right card: CTA', label_el: 'Δεξί card: CTA', type: 'text' },
    ]
  },
  {
    key: 'footer',
    title_en: 'Footer',
    title_el: 'Υποσέλιδο',
    fields: [
      { name: 'tagline', label_en: 'Legal/Contact Tagline', label_el: 'Νομική/Επικοινωνία', type: 'textarea' },
      { name: 'disclaimer', label_en: 'Disclaimer', label_el: 'Αποποίηση Ευθύνης', type: 'textarea' },
      { name: 'copyright', label_en: 'Copyright', label_el: 'Πνευματικά Δικαιώματα', type: 'text' },
    ]
  },
];

const inputCls = "bg-white/5 border-white/10 text-white placeholder-white/40 focus-visible:ring-cyan-400/30";
const textareaCls = "w-full bg-white/5 border border-white/10 rounded-md p-2 text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 text-sm";

const AdminContentTab = ({ T, language }) => {
  const [contentByKey, setContentByKey] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        SECTIONS.map((s) => authFetch(`/api/settings/${s.key}`).then((r) => (r.ok ? r.json() : null)))
      );
      const next = {};
      SECTIONS.forEach((s, i) => {
        next[s.key] = (results[i] && results[i].value) || {};
      });
      setContentByKey(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateField = (sectionKey, fieldName, lang, val) => {
    setContentByKey((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [`${fieldName}_${lang}`]: val }
    }));
  };

  const saveSection = async (sectionKey) => {
    setSavingKey(sectionKey);
    try {
      const res = await authFetch(`/api/admin/settings/${sectionKey}`, {
        method: 'PUT',
        body: JSON.stringify({ value: contentByKey[sectionKey] || {} }),
      });
      if (!res.ok) throw await res.json();
      toast.success(T.saveSuccess);
    } catch (err) {
      toast.error(formatApiError(err?.detail) || T.error);
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <p className="text-white/60">{T.loading}</p>;

  return (
    <div className="space-y-4" data-testid="admin-content-list">
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={fetchAll} className="text-white/70 hover:text-white" data-testid="content-refresh">
          <RefreshCw size={14} className="mr-2" /> {T.refresh}
        </Button>
      </div>
      <Accordion type="multiple" className="space-y-3">
        {SECTIONS.map((section) => {
          const data = contentByKey[section.key] || {};
          return (
            <AccordionItem key={section.key} value={section.key} className="bg-white/5 border border-white/10 rounded-lg px-5" data-testid={`content-section-${section.key}`}>
              <AccordionTrigger className="text-white hover:text-cyan-400">
                {language === 'el' ? section.title_el : section.title_en}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2 pb-3">
                  {section.fields.map((f) => (
                    <div key={f.name} className="grid md:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-white/70 text-xs">{f.label_en} (EN)</Label>
                        {f.type === 'textarea' ? (
                          <textarea
                            rows={3}
                            className={textareaCls}
                            value={data[`${f.name}_en`] || ''}
                            onChange={(e) => updateField(section.key, f.name, 'en', e.target.value)}
                            data-testid={`content-${section.key}-${f.name}-en`}
                          />
                        ) : (
                          <Input
                            className={inputCls}
                            value={data[`${f.name}_en`] || ''}
                            onChange={(e) => updateField(section.key, f.name, 'en', e.target.value)}
                            data-testid={`content-${section.key}-${f.name}-en`}
                          />
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-white/70 text-xs">{language === 'el' ? f.label_el : f.label_en} (EL)</Label>
                        {f.type === 'textarea' ? (
                          <textarea
                            rows={3}
                            className={textareaCls}
                            value={data[`${f.name}_el`] || ''}
                            onChange={(e) => updateField(section.key, f.name, 'el', e.target.value)}
                            data-testid={`content-${section.key}-${f.name}-el`}
                          />
                        ) : (
                          <Input
                            className={inputCls}
                            value={data[`${f.name}_el`] || ''}
                            onChange={(e) => updateField(section.key, f.name, 'el', e.target.value)}
                            data-testid={`content-${section.key}-${f.name}-el`}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={() => saveSection(section.key)}
                      disabled={savingKey === section.key}
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                      data-testid={`content-save-${section.key}`}
                    >
                      <Save size={14} className="mr-2" />
                      {savingKey === section.key ? '...' : T.save}
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default AdminContentTab;
